import { Component } from "react";
import { connect, type ConnectedProps } from "react-redux";

import { bindActionCreators } from "@reduxjs/toolkit";
import { withRouter } from "../../../../../adapters/withRouter/withRouter";
import navigationUtils from "../../../../../utils/navigationUtils";
import WizardStepsManager from "../../../../../utils/WizardStepsManager";
import { type AppDispatch, type RootState } from "../../../../Application/globaltypes/redux";
import * as rtnEvents from "../../../../Application/services/realTimeNotification/events/library/libraryEvents";
import RtnEventsEmitter from "../../../../Application/services/realTimeNotification/rtnEventsEmitter";
import { WizardWrapper as Wizard } from "../../../../WizardWrapper";
import { PublishAndSendWizardButtons } from "../../../components/PublishAndSendWizardButtons/PublishAndSendWizardButtons";
import { EmailCreateSteps } from "../Edit/types";
import Configure from "../EmailDetails/Configure/Configure";
import Content from "../EmailDetails/EmailContent/Content";
import * as emailsOverviewActions from "../state/actions/emailsOverviewActions";
import {
  resetAppliedFilter,
  resetPagination,
  resetSortingColumnName,
  resetSortingDirection,
} from "../state/slices/emailFilterSlice";
import { setTerm } from "../state/slices/emailSearchSlice";
import { type CreateEmailProps, type CreateEmailState } from "./types";
import { type LDProps } from "../../../../LDProps";
import AddAssetsToPackModal from "components/modal/AddAssetsToPacksModal/AddAssetsToPackModal";
import { RouteNames } from "enums";
import { withLDConsumer } from "launchdarkly-react-client-sdk";

const getCreateSendSessionUrl = (emailId: number) =>
  `/${RouteNames.contentCommunications}/emails/${emailId}/send-sessions/create`;

export type CreateEmailPropsAll = CreateEmailProps & PropsFromRedux & LDProps;

export class CreateEmail extends Component<CreateEmailPropsAll, CreateEmailState> {
  private readonly stepsManager = new WizardStepsManager();
  private redirectToCreateSendSessionPage = false;

  constructor(props: CreateEmailPropsAll) {
    super(props);
    this.state = {
      showAddPacksModal: false,
      isValidInfo: false,
      isValidContent: false,
      activeStepIndex: EmailCreateSteps.Configuration,
    };
    this.stepsManager.subscribeOnActiveIndexChanged((_: any, activeStepIndex: number) => {
      this.setState({ activeStepIndex });
    });
  }

  componentDidMount() {
    RtnEventsEmitter.subscribe([rtnEvents.EmailPublishSuccess], this.onPublishedEvent);
  }

  componentWillUnmount() {
    this.stepsManager.dispose();
    RtnEventsEmitter.unsubscribe([rtnEvents.EmailPublishSuccess], this.onPublishedEvent);

    if (!window.location.pathname.includes(`/${RouteNames.contentCommunications}`)) this.props.resetSearchFilters();
  }

  navigateToEmailOrSessionCreation = () =>
    this.props.navigate(
      this.redirectToCreateSendSessionPage
        ? getCreateSendSessionUrl(this.props.id)
        : `/${RouteNames.contentCommunications}`,
    );

  onPublishedEvent = () => this.setState({ showAddPacksModal: true });

  onCancel = () => {
    navigationUtils.goBackOrDefault(this.props.location, this.props.navigate, `/${RouteNames.contentCommunications}`);
  };

  onIsValidInfoChange = (isValidInfo: boolean) => {
    this.setState({ isValidInfo });
  };

  onIsValidContentChange = (isValidContent: boolean) => {
    this.setState({ isValidContent });
  };

  getPublishAndSendHandler = (onFinishAsync: () => Promise<void>) => () => {
    this.redirectToCreateSendSessionPage = true;
    return onFinishAsync();
  };

  renderPublishAndSendButtons = (onFinishAsync: () => Promise<void>) => {
    return (
      <PublishAndSendWizardButtons
        onFinishAsync={onFinishAsync}
        getPublishAndSendHandler={this.getPublishAndSendHandler}
        isValid={this.state.isValidInfo && this.state.isValidContent}
      />
    );
  };

  render() {
    const { isValidInfo, isValidContent, showAddPacksModal } = this.state;
    const { isSaving, dateModified } = this.props;

    return (
      <>
        <Wizard
          title="Create Email"
          renderCustomFinishButton={this.renderPublishAndSendButtons}
          onCancel={this.onCancel}
          onProgressAsync={(_: any, nextIndex: number) => this.stepsManager.onNext(nextIndex)}
          onRegressAsync={(_: any, nextIndex: number) => this.stepsManager.onPrevious(nextIndex)}
          onFinishAsync={this.stepsManager.onFinish}
          isSaveInProgress={isSaving}
          progressSavedDate={dateModified}
          className="create-email"
        >
          <Wizard.Step label="Configure" className="scrollable-content" isLocked={!isValidInfo} required>
            <Configure
              acceptHandlers={(handlers) => this.stepsManager.acceptHandlers(handlers, EmailCreateSteps.Configuration)}
              onIsValidChange={this.onIsValidInfoChange}
              disabled={false}
            />
          </Wizard.Step>
          <Wizard.Step
            label="Content"
            className="scrollable-content edit-email-content"
            isLocked={!isValidContent}
            preRender
          >
            <Content
              acceptHandlers={(handlers) => this.stepsManager.acceptHandlers(handlers, EmailCreateSteps.Content)}
              onIsValidChange={this.onIsValidContentChange}
              disabled={false}
              emailCreating
            />
          </Wizard.Step>
        </Wizard>
        <AddAssetsToPackModal
          showModal={showAddPacksModal}
          onClose={/* istanbul ignore next */ () => this.navigateToEmailOrSessionCreation()}
          onComplete={/* istanbul ignore next */ () => this.navigateToEmailOrSessionCreation()}
          selectedItemIds={[this.props.id]}
          contentType={"Email"}
        />
      </>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  id: state.library.emails.emailEntityStateReducer.entityId,
  isSaving: state.library.emails.emailEntityStateReducer.isEntityCommandInProgress,
  dateModified: state.library.emails.emailEntityStateReducer.lastModifiedDateTime,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  emailsOverviewActions: bindActionCreators(emailsOverviewActions, dispatch),
  resetSearchFilters: () => {
    emailsOverviewActions.resetEmails();
    dispatch(setTerm(""));
    dispatch(resetPagination());
    dispatch(resetSortingColumnName());
    dispatch(resetSortingDirection());
    dispatch(resetAppliedFilter());
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(withRouter(withLDConsumer()(CreateEmail)));
export default ConnectedComponent;
